/**
 * Unified Task Card Component
 * 
 * Shared task card component used by both Kanban and List views.
 * Provides consistent appearance and behavior across all interfaces.
 */

import React, { useState, useCallback, memo, useMemo } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Avatar,
  AvatarGroup,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
  Tooltip,
  Badge
} from '@mui/material';
import InlineEditFieldWithMentions from './components/InlineEditFieldWithMentions';
import {
  MoreVert as MoreVertIcon,
  AccessTime as TimeIcon,
  Flag as FlagIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Comment as CommentIcon,
  Attachment as AttachmentIcon,
  Assignment as AssignmentIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { Draggable } from '@hello-pangea/dnd';
import { format, isToday, isTomorrow, isPast } from 'date-fns';

import PriorityBadge from '../ui/PriorityBadge';
import StatusChip from '../ui/StatusChip';
import SecureAvatar from '../ui/SecureAvatar';
import PendingOperationIndicator from './PendingOperationIndicator';
import TaskCardTimer from './components/TaskCardTimer';
import useTaskStore from '../../stores/taskStore';
import useAuthStore from '../../stores/authStore';
import { PLATFORM_OPTIONS } from '../features/content/form-utils/contentFormData';

const UnifiedTaskCard = memo(({
  task,
  index,
  viewType = 'kanban', // 'kanban' | 'list'
  isDragDisabled = false,
  onTaskClick,
  onStatusChange,
  onPriorityChange,
  onAssigneeChange,
  onQuickEdit,
  onDelete,
  onTimeUpdate,
  showProject = false,
  compact = false,
  className = '',
  viewAsUserId = null // For View As mode
}) => {
  const theme = useTheme();
  const user = useAuthStore(state => state.user);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  
  // Check if task has pending operations
  const isPendingOperation = useTaskStore(state => 
    state.isPendingOperation(task._id, 'task:move') ||
    state.isPendingOperation(task._id, 'task:update') ||
    state.isPendingOperation(task._id, 'section:reorder')
  );

  // ========================================
  // EVENT HANDLERS
  // ========================================

  const handleMenuClick = useCallback((event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleCardClick = useCallback((event) => {
    // Prevent click during drag
    if (event.defaultPrevented) return;
    
    // Don't handle clicks on menu button
    if (event.target.closest('.task-menu-button')) return;
    
    // Don't handle if we're clicking on interactive elements
    if (event.target.closest('button, input, textarea, select')) return;
    
    onTaskClick?.(task);
  }, [task, onTaskClick]);

  const handleStatusToggle = useCallback((event) => {
    event.stopPropagation();
    const newStatus = task.status === 'done' ? 'to_do' : 'done';
    onStatusChange?.(task._id, newStatus);
  }, [task._id, task.status, onStatusChange]);

  const handleMenuAction = useCallback((action) => {
    handleMenuClose();
    
    switch (action) {
      case 'edit':
        onQuickEdit?.(task);
        break;
      case 'delete':
        onDelete?.(task._id);
        break;
      case 'priority-high':
        onPriorityChange?.(task._id, 'high');
        break;
      case 'priority-medium':
        onPriorityChange?.(task._id, 'medium');
        break;
      case 'priority-low':
        onPriorityChange?.(task._id, 'low');
        break;
      default:
        break;
    }
  }, [task, onQuickEdit, onDelete, onPriorityChange]);

  // ========================================
  // COMPUTED VALUES
  // ========================================

  const completionPercentage = useMemo(() => {
    if (task.status === 'done') return 100;
    if (!task.checklist || task.checklist.length === 0) return 0;
    
    const completed = task.checklist.filter(item => item.isCompleted).length;
    return Math.round((completed / task.checklist.length) * 100);
  }, [task.status, task.checklist]);

  const dueDateInfo = useMemo(() => {
    // Compute effective deadline: use collaborator's deadline if user is a collaborator, otherwise task's due date
    // In View As mode, use the viewAsUserId, otherwise use current user
    const targetUserId = viewAsUserId || user?._id;
    const collaborator = task.collaborators?.find(c => 
      c.user?._id === targetUserId || c.user === targetUserId
    );
    const effectiveDueDate = collaborator?.deadline || task.dueDate;
    if (!effectiveDueDate) return null;
    
    const dueDate = new Date(effectiveDueDate);
    const now = new Date();
    
    let label, color;
    
    if (isPast(dueDate) && task.status !== 'done') {
      label = `Overdue ${format(dueDate, 'MMM d')}`;
      color = 'error';
    } else if (isToday(dueDate)) {
      label = 'Due Today';
      color = 'warning';
    } else if (isTomorrow(dueDate)) {
      label = 'Due Tomorrow';
      color = 'info';
    } else {
      label = format(dueDate, 'MMM d');
      color = 'default';
    }
    
    return { label, color, date: dueDate };
  }, [task.collaborators, task.dueDate, task.status, viewAsUserId, user?._id]);

  const hasAttachments = (task.attachmentCount !== undefined ? task.attachmentCount : task.attachments?.length) > 0;
  const hasComments = (task.commentCount !== undefined ? task.commentCount : task.comments?.length) > 0;
  const hasChecklist = task.checklist && task.checklist.length > 0;
  const hasSubtasks = (task.subtaskCount !== undefined ? task.subtaskCount : task.subtasks?.length) > 0;
  
  // Extract platforms from content distributions
  const platforms = useMemo(() => {
    if (!task.content || typeof task.content !== 'object') {
      return [];
    }
    // Use virtual platforms field if available, otherwise extract from distributions
    return task.content.platforms || (task.content.distributions ? task.content.distributions.map(d => d.platform) : []);
  }, [task.content]);

  // ========================================
  // RENDER HELPERS
  // ========================================

  const renderTaskContent = () => (
    <CardContent 
      sx={{ 
        p: compact ? 1.5 : 2,
        '&:last-child': { pb: compact ? 1.5 : 2 }
      }}
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box display="flex" alignItems="center" gap={1} flex={1}>
          {/* Status Toggle */}
          <IconButton
            size="small"
            onClick={handleStatusToggle}
            onMouseDown={(e) => e.stopPropagation()}
            sx={{ p: 0.5 }}
          >
            {task.status === 'done' ? (
              <CheckCircleIcon color="success" fontSize="small" />
            ) : (
              <RadioButtonUncheckedIcon color="action" fontSize="small" />
            )}
          </IconButton>

          {/* Task Title */}
          <Typography
            variant={compact ? "body2" : "subtitle2"}
            fontWeight={task.status === 'done' ? 400 : 600}
            sx={{
              textDecoration: task.status === 'done' ? 'line-through' : 'none',
              opacity: task.status === 'done' ? 0.7 : 1,
              cursor: 'inherit',
              flex: 1,
              userSelect: 'none'
            }}
            noWrap
          >
            {task.title}
          </Typography>
        </Box>

        {/* Menu Button */}
        <IconButton
          size="small"
          onClick={handleMenuClick}
          onMouseDown={(e) => e.stopPropagation()}
          className="task-menu-button"
          sx={{ 
            opacity: isHovered ? 1 : 0.3,
            transition: 'opacity 0.2s'
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Project, Section and Brand Info (for list view) */}
      {showProject && task.projectId && (
        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
          {task.projectId.name}
          {task.sectionId?.name && ` → ${task.sectionId.name}`}
          {task.projectId.brandId?.name && ` • ${task.projectId.brandId.name}`}
        </Typography>
      )}

      {/* Description */}
      {task.description && !compact && (
        <Box sx={{ 
          mb: 1.5,
          '& > div': {
            padding: 0,
            minHeight: 'auto',
            maxHeight: '60px',
            overflow: 'hidden',
            border: 'none',
            '&:hover': {
              backgroundColor: 'transparent',
              border: 'none',
            },
          },
          '& .MuiTypography-root': {
            fontSize: '0.875rem',
            color: theme.palette.text.secondary,
          },
          '& ul, & ol': {
            margin: '4px 0',
            paddingLeft: '20px',
          },
          '& li': {
            marginBottom: '2px',
            fontSize: '0.875rem',
          },
        }}>
          <InlineEditFieldWithMentions
            value={task.description}
            onChange={() => {}} // No-op since it's read-only
            disabled={true}
            placeholder=""
            variant="description"
            multiline={false}
            rows={2}
          />
        </Box>
      )}

      {/* Collaborators - subtle and clean */}
      {((task.collaboratorCount !== undefined ? task.collaboratorCount : task.collaborators?.length) > 0) && (
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            mb: 1
          }}
        >
          <Box display="flex" alignItems="center" sx={{ ml: 0.5 }}>
            {/* Small group icon to indicate collaborators */}
            <Tooltip title="Team members collaborating">
              <GroupIcon 
                sx={{ 
                  fontSize: 16, 
                  color: theme.palette.text.secondary,
                  mr: 0.5
                }} 
              />
            </Tooltip>
            
            {/* Collaborator avatars with status indicators */}
            {(task.collaborators || []).slice(0, compact ? 3 : 5).map((collab, idx) => {
              const user = collab.user || {};
              const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unknown';
              const status = collab.status || 'to_do';
              const statusColor = status === 'done' ? theme.palette.success.main : 
                               status === 'in_progress' ? theme.palette.info.main : 
                               theme.palette.action.disabled;
              const statusLabel = status === 'done' ? '✓ Done' : 
                                status === 'in_progress' ? '⚡ In Progress' : 
                                '○ To Do';
              return (
                <Tooltip 
                  key={idx} 
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        sx={{ 
                          fontSize: '0.85rem'
                        }}
                      >
                        {fullName}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        display="block" 
                        sx={{ 
                          opacity: 0.9,
                          fontSize: '0.75rem'
                        }}
                      >
                        {statusLabel}
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                  enterDelay={200}
                  leaveDelay={0}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 
                          alpha(theme.palette.background.paper, 0.95),
                        color: theme.palette.mode === 'dark'
                          ? theme.palette.text.primary
                          : theme.palette.primary.contrastText,
                        backdropFilter: 'blur(8px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        '& .MuiTooltip-arrow': {
                          color: theme.palette.mode === 'dark' 
                            ? alpha(theme.palette.background.paper, 0.95)
                            : theme.palette.primary.main,
                          '&::before': {
                            border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          }
                        },
                      },
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      ml: idx > 0 ? -0.8 : 0,
                      zIndex: task.collaborators.length - idx,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        zIndex: 999
                      }
                    }}
                  >
                    <SecureAvatar
                      user={user}
                      size={compact ? 22 : 24}
                      sx={{ 
                        border: `2px solid ${statusColor}`,
                        opacity: status === 'done' ? 0.9 : 1,
                        cursor: 'pointer'
                      }}
                    />
                  </Box>
                </Tooltip>
              );
            })}

            {/* More collaborators indicator */}
            {(task.collaboratorCount !== undefined ? task.collaboratorCount : task.collaborators?.length || 0) > (compact ? 3 : 5) && (
              <Tooltip title={`+${(task.collaboratorCount !== undefined ? task.collaboratorCount : task.collaborators?.length || 0) - (compact ? 3 : 5)} more collaborators`}>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500
                  }}
                >
                  +{(task.collaboratorCount !== undefined ? task.collaboratorCount : task.collaborators?.length || 0) - (compact ? 3 : 5)}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}

      {/* Progress Bar */}
      {hasChecklist && completionPercentage > 0 && (
        <Box mb={1.5}>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.grey[200]
            }}
          />
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            {completionPercentage}% complete
          </Typography>
        </Box>
      )}

      {/* Metadata Row */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        {/* Left side - Status, Priority, Due Date */}
        <Box display="flex" alignItems="center" gap={1} flex={1} flexWrap="wrap">
          <StatusChip status={task.status} size="small" />
          
          {task.priority !== 'medium' && (
            <PriorityBadge priority={task.priority} size="small" />
          )}
          
          {dueDateInfo && (
            <Chip
              label={dueDateInfo.label}
              size="small"
              color={dueDateInfo.color}
              variant="outlined"
              icon={<TimeIcon />}
              sx={{ fontSize: '0.7rem' }}
            />
          )}
          
          {/* Platform chips */}
          {platforms.length > 0 && (
            <Box display="flex" gap={0.5}>
              {platforms.slice(0, compact ? 2 : 3).map((platformId) => {
                const platform = PLATFORM_OPTIONS.find(p => p.value === platformId);
                if (!platform) return null;
                
                return (
                  <Chip
                    key={platformId}
                    label={platform.label}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      backgroundColor: platform.color,
                      color: 'white',
                      '& .MuiChip-label': {
                        px: 0.8
                      }
                    }}
                  />
                );
              })}
              {platforms.length > (compact ? 2 : 3) && (
                <Chip
                  label={`+${platforms.length - (compact ? 2 : 3)}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.65rem',
                    '& .MuiChip-label': {
                      px: 0.8
                    }
                  }}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Right side - Indicators and Assignee */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {/* Indicators */}
          {hasComments && (
            <Badge badgeContent={task.commentCount !== undefined ? task.commentCount : task.comments?.length} color="primary" max={9}>
              <CommentIcon fontSize="small" color="action" />
            </Badge>
          )}

          {hasAttachments && (
            <Badge badgeContent={task.attachmentCount !== undefined ? task.attachmentCount : task.attachments?.length} color="primary" max={9}>
              <AttachmentIcon fontSize="small" color="action" />
            </Badge>
          )}

          {hasSubtasks && (
            <Badge badgeContent={task.subtaskCount !== undefined ? task.subtaskCount : task.subtasks?.length} color="primary" max={9}>
              <AssignmentIcon fontSize="small" color="action" />
            </Badge>
          )}

          {/* Time Tracker */}
          <TaskCardTimer 
            task={task} 
            compact={true}
            onTimeUpdate={onTimeUpdate}
          />

          {/* Assignee - larger and primary color */}
          {task.assignedTo && task.assignedTo._id && (
            <Tooltip 
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      opacity: 0.8,
                      fontSize: '0.7rem'
                    }}
                  >
                    Assigned to
                  </Typography>
                  <Typography 
                    variant="body2" 
                    fontWeight={600} 
                    sx={{ 
                      fontSize: '0.85rem'
                    }}
                  >
                    {`${task.assignedTo.firstName || ''} ${task.assignedTo.lastName || ''}`.trim() || 'Unknown User'}
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
              enterDelay={200}
              leaveDelay={0}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: alpha(theme.palette.background.paper, 0.95),
                    color: theme.palette.mode === 'dark'
                      ? theme.palette.text.primary
                      : theme.palette.primary.contrastText,
                    backdropFilter: 'blur(8px)',
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                    '& .MuiTooltip-arrow': {
                      color: theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.background.paper, 0.95)
                        : theme.palette.primary.main,
                      '&::before': {
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                      }
                    },
                  },
                },
              }}
            >
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  ml: 0.5,
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <SecureAvatar
                  user={task.assignedTo}
                  size={compact ? 26 : 30}
                  sx={{ 
                    border: `2px solid ${theme.palette.primary.main}`,
                    cursor: 'pointer'
                  }}
                />
              </Box>
            </Tooltip>
          )}
        </Box>
      </Box>
    </CardContent>
  );

  const renderMenu = () => (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleMenuClose}
      onClick={(e) => e.stopPropagation()}
    >
      <MenuItem onClick={() => handleMenuAction('edit')}>
        Quick Edit
      </MenuItem>
      {/* <MenuItem onClick={() => handleMenuAction('priority-high')}>
        Set High Priority
      </MenuItem>
      <MenuItem onClick={() => handleMenuAction('priority-medium')}>
        Set Medium Priority
      </MenuItem>
      <MenuItem onClick={() => handleMenuAction('priority-low')}>
        Set Low Priority
      </MenuItem> */}
      <MenuItem onClick={() => handleMenuAction('delete')} sx={{ color: 'error.main' }}>
        Delete Task
      </MenuItem>
    </Menu>
  );

  // ========================================
  // RENDER
  // ========================================

  const cardContent = (
    <PendingOperationIndicator isPending={isPendingOperation}>
      <Card
        className={`unified-task-card ${className}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleCardClick}
        sx={{
          cursor: isDragDisabled ? 'pointer' : 'grab',
          transition: 'all 0.2s ease-in-out',
          border: `1px solid ${theme.palette.divider}`,
          '&:hover': {
            boxShadow: theme.shadows[4],
            // Disable transform on hover when drag is enabled to prevent conflicts
            transform: viewType === 'kanban' && isDragDisabled ? 'translateY(-1px)' : 'none',
            borderColor: !isDragDisabled ? theme.palette.primary.main : theme.palette.divider
          },
          opacity: task.isOptimistic ? 0.7 : 1,
          position: 'relative',
          ...(task.status === 'done' && {
            backgroundColor: theme.palette.mode === 'dark' 
              ? theme.palette.action.hover
              : theme.palette.action.selected,
            opacity: 0.9
          }),
          ...(viewType === 'list' && {
            mb: 1,
            borderRadius: 1
          })
        }}
      >
        {renderTaskContent()}
        {renderMenu()}
      </Card>
    </PendingOperationIndicator>
  );

  // Wrap with Draggable for both Kanban and List views when drag is enabled
  if (!isDragDisabled) {
    return (
      <Draggable
        draggableId={task._id}
        index={index}
        isDragDisabled={isDragDisabled || task.isOptimistic}
      >
        {(provided, snapshot) => {
          // Debug logging
          if (snapshot.isDragging) {
            console.log('UnifiedTaskCard: Currently dragging', task._id);
          }
          
          return (
            <div
              ref={provided.innerRef}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
              style={{
                ...provided.draggableProps.style,
                marginBottom: viewType === 'kanban' ? '8px' : '4px'
              }}
            >
              {cardContent}
            </div>
          );
        }}
      </Draggable>
    );
  }

  // Return plain card for List view
  return cardContent;
});

UnifiedTaskCard.displayName = 'UnifiedTaskCard';

export default UnifiedTaskCard;